import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { detectApp, detectSandkastenApp, getPostLoginPath, sandkastenPortAusUrl } from '../../../shared/appDetect';
import { accountEnvironmentMatches, loginInPage, postLoginTarget } from '../../../shared/appSession';
import {
  defaultKey, gemerkteAppZugaenge, identitaetFehler, kontoAnzahl, ladeAppZugaenge, vergissAppZugaenge, zugaengeFuerSatz,
  type AppCreds, type CredUser, type Freigabe, type ProdUser,
} from './appZugaenge';
import { useAuth } from '../../contexts/AuthContext';

// Slide-in inside a BrowserPanel: when the webview shows a Werkingflow app,
// offer one-click test-user login. A default user auto-logs-in on the /login
// page; otherwise the user list is shown. Spares the manual credential copy.


interface AppLoginPanelProps {
  /** Detected app key (credentials.json key), or null if not an app. */
  appKey: string | null;
  /** True when the webview currently sits on the app's /login page. */
  isLoginPage: boolean;
  /** True when the webview shows a PRODUCTION URL → drives the toggle's active
   *  side and which credential set the picker shows. */
  isProduction: boolean;
  /**
   * Welcher Zugangsdaten-Satz auf dieser Adresse funktioniert (aus detectApp).
   * Getrennt von `isProduction`, weil Staging eine deployte Adresse ist und
   * trotzdem die dev-Bridge spricht — die alte Gleichsetzung bot dort genau die
   * Konten an, die 401 liefern.
   */
  credentialSet: 'production' | 'dev';
  /** Platz in der Adresszeile des Browser-Panels fuer den eingeklappten Login-Knopf (null = Adresszeile zu). */
  adresszeilenPlatz?: HTMLElement | null;
  /** The app's local/partner URL, so the Local/Prod toggle can navigate back to
   *  the local instance. null if the app has no known dev port. */
  localUrl: string | null;
  /**
   * Port eines Partner-Sandkastens, dessen App der CUI-Server per /api/version
   * erkannt hat (sonst null). Die Pruefung vor dem Login verlangt dann, dass die
   * Webview noch auf GENAU diesem Port steht — detectApp() allein kennt ihn nicht.
   */
  sandkastenPort?: number | null;
  webviewRef: React.RefObject<HTMLWebViewElement | null>;
  /**
   * Kuerzel des Kontos aus Rafaels Freigabeliste (Guide, `konfig.konten`), als
   * das diese Kachel zeigen soll. Gesetzt heisst: selbst anmelden, ohne Klick —
   * auch auf Staging und Produktion. Das ist der Unterschied zum
   * localStorage-Standard unten: DER ist eine Bequemlichkeit des Bedieners und
   * bleibt deshalb auf lokale Instanzen beschraenkt; DIESER hier ist eine
   * ausdrueckliche Freigabe, die Rafael pro Konto erteilt hat und im Guide sieht.
   */
  konto?: string | null;
  /** Vercel-Staging (detectApp().isStaging). Sperrt fuer Partner Engelmann-Staging. */
  isStaging?: boolean;
}

export default function AppLoginPanel({ appKey, isLoginPage, isProduction, credentialSet, localUrl, sandkastenPort = null, webviewRef, konto, isStaging = false, adresszeilenPlatz = null }: AppLoginPanelProps) {
  const { user: angemeldet, authEnabled } = useAuth();
  /* Rafael, 05.10.2026 (e-mo-logins-umfang-20261005 = a): Partner sehen Demo und Staging,
   * aber NICHT Engelmann-Staging — es haengt an der Produktions-Datenbank (echte Kundendaten). */
  const gesperrt = isStaging && appKey === 'engelmann-ai-hub' && authEnabled === true && !!angemeldet && angemeldet.role !== 'admin';
  const [apps, setApps] = useState<Record<string, AppCreds> | null>(gemerkteAppZugaenge());
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [defaultEmail, setDefaultEmail] = useState<string | null>(null);
  const autoTriedRef = useRef<string | null>(null);
  // Production mode: live account directory from the Prod-Bridge + create form.
  const [prodUsers, setProdUsers] = useState<ProdUser[] | null>(null);
  const [prodState, setProdState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [prodError, setProdError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '' });
  const [creating, setCreating] = useState(false);
  // Das freigegebene Konto aus dem Guide (`konfig.konten`), sobald `konto` gesetzt
  // ist. null = noch nicht geladen, 'unbekannt' = geladen und nicht gefunden.
  const [freigabe, setFreigabe] = useState<Freigabe | null | 'unbekannt'>(null);
  const kontoTriedRef = useRef<string | null>(null);
  /** Zaehlt hoch, sobald die Webview ansprechbar ist (dom-ready/did-navigate). */
  const [webviewTakt, setWebviewTakt] = useState(0);

  // Das Passwort liegt hier ohnehin im Client — die Liste braucht es zum
  // Einloggen. Ablesbar war es nur nicht, und wer Zugangsdaten an einen Kunden
  // weitergibt, braucht genau das. Pro Zeile aufklappbar, nie offen by default.
  const [pwOffen, setPwOffen] = useState<Record<string, boolean>>({});
  const [kopiert, setKopiert] = useState<string | null>(null);

  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const [credentialsReload, setCredentialsReload] = useState(0);
  // A failed lookup must stay visible and retryable, never poison the shared cache.
  useEffect(() => {
    const gemerkt = gemerkteAppZugaenge();
    if (gemerkt) { setApps(gemerkt); return; }
    let active = true;
    setCredentialsError(null);
    ladeAppZugaenge()
      .then(a => {
        if (!active) return;
        setApps(a);
        // Laut statt still auf ein Testkonto auszuweichen: die eigene Identitaet ist eingetragen, aber kaputt.
        const fehler = identitaetFehler();
        if (fehler) setStatus(`Eigene Staging-Identität nicht nutzbar: ${fehler}`);
      })
      .catch(e => { if (active) setCredentialsError(e instanceof Error ? e.message : String(e)); });
    return () => { active = false; };
  }, [credentialsReload]);

  // Track the per-app default; reset the auto-login guard when the app changes.
  useEffect(() => {
    if (autoTriedRef.current && autoTriedRef.current !== appKey) autoTriedRef.current = null;
    setDefaultEmail(appKey ? localStorage.getItem(defaultKey(appKey)) : null);
  }, [appKey]);

  const appCreds = appKey && apps ? apps[appKey] : null;
  // Show only the credential set matching the webview environment: on a PRODUCTION
  // URL the real Prod-Bridge accounts (env:'production', from prod-credentials.json),
  // on dev/partner the test+interactive users (env:'dev'). The two sets live in
  // different Bridge DBs — test accounts deliberately do NOT exist on the Prod-
  // Bridge, so offering them on a prod URL would just 401. Real prod demo users
  // belong in prod-credentials.json; test users stay local-only.
  const istProdSatz = credentialSet === 'production';
  const envUsers = appCreds && !gesperrt ? zugaengeFuerSatz(appCreds.users, credentialSet) : [];

  const doLogin = useCallback(async (user: Pick<CredUser, 'email' | 'password' | 'name'> & Partial<CredUser>): Promise<boolean> => {
    const wv = webviewRef.current as unknown as {
      executeJavaScript?: (c: string) => Promise<unknown>;
      reload?: () => void;
      getURL?: () => string;
      loadURL?: (url: string) => Promise<void>;
    } | null;
    if (!wv?.executeJavaScript) { setStatus('Webview nicht bereit'); setOpen(true); return false; }
    const current = wv.getURL?.() || '';
    const detected = detectApp(current)
      ?? (sandkastenPort !== null && sandkastenPortAusUrl(current) === sandkastenPort ? detectSandkastenApp(current, appKey) : null);
    if (!detected || detected.appKey !== appKey || detected.credentialSet !== credentialSet) {
      setStatus('App oder Umgebung hat sich geändert — keine Anmeldung ausgeführt.'); setOpen(true); return true;
    }
    setStatus(`Prüfe bestehende Anmeldung als ${user.name || user.email} …`);
    const code = `(${loginInPage.toString()})(${JSON.stringify({ origin: new URL(current).origin, email: user.email, password: user.password })})`;
    let parsed: { ok: boolean; status: number; reused?: boolean; body?: string; error?: string; pageUrl?: string };
    try {
      const result = await wv.executeJavaScript(code);
      try { parsed = result as typeof parsed; if (!parsed || typeof parsed.ok !== 'boolean') throw Error(); }
      catch { parsed = { ok: false, status: -1, error: 'Ungültige Antwort der Sitzungsprüfung' }; }
    } catch (e) {
      parsed = { ok: false, status: -2, error: 'Webview-Sitzungsprüfung nicht erreichbar.' };
    }
    console.log('[AppLogin] doLogin result:', parsed);
    if (parsed.ok) {
      setStatus('Eingeloggt — öffne App …');
      // Navigate to the app-specific post-login path. NOT '/' — most apps'
      // root page.tsx redirects unauth'd users to /login based on a stale
      // localStorage check, and Q-Login only sets the HttpOnly cookie, so
      // root would bounce us back to /login and look like a failed login.
      try {
        const cur = wv.getURL?.() || '';
        const target = cur ? postLoginTarget(cur, appKey ? getPostLoginPath(appKey) : '/') : '';
        if (target && wv.loadURL) {
          if (target !== cur || !parsed.reused) await wv.loadURL(target);
        } else if (!parsed.reused) wv.reload?.();
      } catch {
        wv.reload?.();
      }
      setOpen(false);
      setTimeout(() => setStatus(''), 2500);
    } else {
      const detail = parsed.body || parsed.error || '';
      setStatus(`Login fehlgeschlagen — HTTP ${parsed.status}${detail ? `: ${detail.slice(0, 100)}` : ''}`);
      // Ohne das bleibt der Zusatz zugeklappt und die Kachel zeigt stumm die
      // Login-Maske — genau der Zustand, den der Kommentar oben ausschliessen
      // wollte und den Rafael am 08.09. dreimal vor sich hatte.
      setOpen(true);
    }
    // -2 = executeJavaScript hat geworfen (Webview noch nicht bereit). Das ist
    // KEIN fachlicher Fehlschlag, sondern ein zu frueher Versuch: der Aufrufer
    // darf ihn wiederholen, sobald die Webview meldet, dass sie steht.
    return parsed.ok || parsed.status !== -2;
  }, [webviewRef, appKey, credentialSet, sandkastenPort]);

  // Local/Prod toggle: navigate the webview to the local instance or the app's
  // production URL. The webview's did-navigate listener (in BrowserPanel) updates
  // the URL state, which re-runs detectApp → isProduction flips → the picker
  // shows the matching credential set. No-op if the target URL is unknown.
  const switchEnv = useCallback((toProd: boolean) => {
    const target = toProd ? appCreds?.productionUrl : localUrl;
    if (!target) { setStatus(toProd ? 'Keine Production-URL hinterlegt' : 'Keine lokale URL'); return; }
    const wv = webviewRef.current as unknown as { loadURL?: (u: string) => Promise<void> } | null;
    if (!wv?.loadURL) { setStatus('Webview nicht bereit'); return; }
    setStatus(toProd ? 'Wechsle zu Production …' : 'Wechsle zu Lokal …');
    void wv.loadURL(target).catch(() => setStatus('Navigation fehlgeschlagen'));
    setOpen(true);
  }, [appCreds, localUrl, webviewRef]);

  // Production mode: load the live account directory from the Prod-Bridge.
  const loadProdUsers = useCallback(async () => {
    if (!appKey) return;
    setProdState('loading');
    setProdError('');
    try {
      const r = await fetch(`/api/app-prod-users/${encodeURIComponent(appKey)}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setProdError(d.detail || d.error || `HTTP ${r.status}`);
        setProdState('error');
        setProdUsers(null);
        return;
      }
      setProdUsers(Array.isArray(d.users) ? d.users : []);
      setProdState('idle');
    } catch (e) {
      setProdError((e as Error).message || String(e));
      setProdState('error');
      setProdUsers(null);
    }
  }, [appKey]);

  // Fetch the prod directory when the panel is open on a production URL.
  useEffect(() => {
    if (open && istProdSatz && appKey) void loadProdUsers();
  }, [open, istProdSatz, appKey, loadProdUsers]);

  // Create a real account on the Prod-Bridge, then log straight in as it.
  const createProdUser = useCallback(async () => {
    if (!appKey) return;
    const name = createForm.name.trim();
    const email = createForm.email.trim();
    const password = createForm.password;
    if (!name || !email || !password) { setStatus('Name, E-Mail und Passwort nötig'); return; }
    setCreating(true);
    setStatus(`Lege ${email} auf Production an …`);
    try {
      const r = await fetch(`/api/app-prod-users/${encodeURIComponent(appKey)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.status === 'created') {
        setStatus(`Angelegt — logge ein als ${email} …`);
        setShowCreate(false);
        setCreateForm({ name: '', email: '', password: '' });
        await loadProdUsers();
        await doLogin({ name, email, password, role: null, primary: false, tester: false, env: 'production' });
      } else if (r.status === 409) {
        setStatus(d.message || 'Account existiert bereits — Passwort unbekannt.');
      } else {
        setStatus(`Anlegen fehlgeschlagen — ${d.detail || d.error || `HTTP ${r.status}`}`);
      }
    } catch (e) {
      setStatus(`Anlegen fehlgeschlagen — ${(e as Error).message || String(e)}`);
    } finally {
      setCreating(false);
    }
  }, [appKey, createForm, loadProdUsers, doLogin]);

  /*
   * DIE BUEHNE MELDET SICH SELBST AN (Rafael, 08.09. 04:44Z)
   * -------------------------------------------------------
   * "Ich will nicht, dass ich mich dann in die Apps einloggen muss und dann
   * wieder haendisch irgendwohin gehe. Sonst waere die Buehne aufbereitet,
   * sodass mich der Nutzer genau dorthin navigiert, wo ich hingehoere."
   *
   * Warum das hier auch auf Produktion feuern DARF, wo der Standard unten es
   * ausdruecklich nicht darf: dort ist es eine Bequemlichkeit, die sich ein
   * Bediener einmal im localStorage gesetzt hat — hier ist es eine Freigabe,
   * die Rafael pro Konto erteilt und im Guide vor sich sieht. Die Freigabe ist
   * genau das Gegenstueck zum Risiko: eine Anmeldung beendet jede andere
   * Sitzung desselben Kontos, also entscheidet er, welche Konten das treffen
   * darf, statt dass es beilaeufig passiert.
   *
   * Jeder Fehlschlag wird ANGEZEIGT, nicht verschluckt: eine Kachel, die
   * stumm die Login-Maske zeigt, ist genau der Zustand, der diesen Umbau
   * ausgeloest hat.
   */
  /*
   * WANN IST DIE WEBVIEW ANSPRECHBAR?
   *
   * `executeJavaScript` existiert am <webview>-Element sofort, funktioniert
   * aber erst nach `dom-ready` — davor wirft es. Der Konto-Login lief bisher
   * beim Mounten los und verbrannte seinen einen Versuch an genau diesem
   * Wettlauf. Dieser Takt zaehlt hoch, sobald die Webview steht oder
   * navigiert; der Login-Effekt haengt daran und probiert es dann erneut.
   *
   * `dom-ready` kann bereits gefeuert haben, bevor dieses Panel ueberhaupt
   * gemountet ist (es erscheint erst, wenn detectApp die Adresse erkennt).
   * Deshalb ist der erste Takt nicht das Ereignis, sondern der Mount selbst —
   * der Versuch laeuft also wie bisher sofort, nur nicht mehr ein einziges Mal.
   */
  useEffect(() => {
    const wv = webviewRef.current as unknown as {
      addEventListener?: (t: string, f: () => void) => void;
      removeEventListener?: (t: string, f: () => void) => void;
    } | null;
    if (!wv?.addEventListener) return;
    const takt = () => setWebviewTakt(n => n + 1);
    wv.addEventListener('dom-ready', takt);
    wv.addEventListener('did-navigate', takt);
    return () => {
      wv.removeEventListener?.('dom-ready', takt);
      wv.removeEventListener?.('did-navigate', takt);
    };
  }, [webviewRef]);

  useEffect(() => {
    if (!konto) { setFreigabe(null); return; }
    let abgebrochen = false;
    fetch('/api/guide/konten')
      .then(r => (r.ok ? r.json() : { konten: [] }))
      .then((d: { konten?: Freigabe[] }) => {
        if (abgebrochen) return;
        setFreigabe((d.konten ?? []).find(k => k.kuerzel === konto) ?? 'unbekannt');
      })
      .catch(() => { if (!abgebrochen) setFreigabe('unbekannt'); });
    return () => { abgebrochen = true; };
  }, [konto]);

  // Auf einer Produktions-/Staging-Adresse liegen die Passwoerter im
  // Prod-Verzeichnis, das sonst erst beim Aufklappen geladen wird. Fuer ein
  // freigegebenes Konto braucht es das VORHER — sonst wartet die Anmeldung auf
  // einen Klick, den es gerade nicht geben soll.
  //
  // Bedingung ist `prodUsers === null`, NICHT `prodState === 'idle'`: der Lader
  // stellt nach Erfolg wieder auf 'idle', ein Zustands-Guard liefe also endlos
  // im Kreis (laden -> idle -> laden). Eine geladene, auch leere, Liste ist der
  // Zustand "schon versucht"; 'error' bleibt stehen und feuert ebenfalls nicht
  // nach.
  useEffect(() => {
    if (konto && istProdSatz && appKey && prodUsers === null && prodState !== 'loading' && prodState !== 'error') {
      void loadProdUsers();
    }
  }, [konto, istProdSatz, appKey, prodUsers, prodState, loadProdUsers]);

  useEffect(() => {
    if (!konto || !appKey || !freigabe) return;
    let origin = '';
    try { origin = new URL((webviewRef.current as unknown as {getURL?:()=>string} | null)?.getURL?.() || '').origin; } catch { /* webview not ready */ }
    const marke = `${appKey}|${konto}|${credentialSet}|${origin}`;
    if (kontoTriedRef.current === marke) return;

    if (freigabe === 'unbekannt') {
      kontoTriedRef.current = marke;
      setStatus(`Konto "${konto}" steht nicht in der Freigabeliste — nicht angemeldet.`);
      setOpen(true);
      return;
    }
    if (freigabe.app && freigabe.app !== appKey) {
      kontoTriedRef.current = marke;
      setStatus(`Konto "${konto}" gehoert zu ${freigabe.app}, diese Kachel zeigt ${appKey} — nicht angemeldet.`);
      setOpen(true);
      return;
    }
    if (!accountEnvironmentMatches(freigabe.umgebung, credentialSet)) {
      kontoTriedRef.current = marke;
      setStatus('Das freigegebene Konto gehört zu einer anderen Umgebung. Passendes Konto wählen.');
      setOpen(true); return;
    }
    // Das Passwort kommt aus dem bestehenden Zugangsspeicher, nicht aus der
    // Freigabeliste. Auf einer Prod-/Staging-Adresse ist das das
    // Prod-Verzeichnis, sonst der dev-Satz aus credentials.json.
    const ausProd = (prodUsers ?? []).find(u => u.email === freigabe.email && u.canLogin && u.password);
    const ausDev = envUsers.find(u => u.email === freigabe.email && u.password);
    const treffer: CredUser | null = istProdSatz
      ? (ausProd ? { name: ausProd.name, email: ausProd.email, password: ausProd.password, role: ausProd.role, primary: false, tester: false, env: 'production' } : null)
      : (ausDev ?? null);

    if (!treffer) {
      // Noch am Laden? Dann NICHT als Fehler abhaken — sonst bliebe es beim
      // ersten Durchlauf stehen und die Kachel meldete sich nie an.
      if (istProdSatz && prodState === 'loading') return;
      if (!istProdSatz && !appCreds) return;
      // A saved password is unnecessary if this profile is already signed in.
    }
    kontoTriedRef.current = marke;
    void doLogin(treffer ?? { email: freigabe.email, password: null, name: freigabe.label }).then(erledigt => {
      // Kam die Webview zu spaet, war das kein Versuch — die Marke wieder
      // freigeben, damit der naechste `webviewTakt` (dom-ready/did-navigate)
      // es erneut probiert. Ohne das verbrennt der EINZIGE Versuch an einem
      // Wettlauf, und die Kachel bleibt fuer immer auf der Login-Maske
      // stehen (gemessen an Rafaels Buehne, 08.09.).
      if (!erledigt) kontoTriedRef.current = null;
    });
  }, [konto, appKey, freigabe, istProdSatz, credentialSet, prodUsers, prodState, envUsers, appCreds, doLogin, webviewTakt]);

  // Auto-login on the /login page when a default user is set. Local mode only —
  // auto-firing a login against PRODUCTION would be a surprising side effect.
  /* STANDARD FUER PARTNER (Rafael, 05.10.2026, als Mo: „stelle sicher, dass er standardmaessig
   * dort eingeloggt ist, wo auch schon Projekte gelaufen sind"): ohne eigene Wahl das Konto mit
   * den meisten gemessenen Daten — auch auf Staging (dev-Bridge, Testdaten), nie in Produktion. */
  const istPartner = authEnabled === true && !!angemeldet && angemeldet.role !== 'admin';
  useEffect(() => {
    if (isProduction && !(istPartner && credentialSet === 'dev')) return;
    // Ein Konto aus der Freigabeliste hat Vorrang: sonst meldete der
    // localStorage-Standard einen anderen Nutzer an, und die Kachel zeigte
    // jemanden, den das Rezept nie genannt hat.
    if (konto) return;
    if (!appKey || !isLoginPage || !appCreds) return;
    if (autoTriedRef.current === appKey) return;
    /* EIGENE IDENTITAET ZUERST (Rafael, 02.11.2026: „jeder user seinen staging account …
     * bei allen apps die fuer ihn freigeschaltet sind anmelden"). Hat der Partner eine
     * persoenliche Staging-Identitaet, meldet ihn das Add-in damit an — auch vor einem
     * frueher gemerkten Standard, der auf ein geteiltes Testkonto zeigen kann. Ob die App
     * fuer ihn freigeschaltet ist, entscheidet die App selbst (Lizenz/Entitlement) und zeigt
     * es sichtbar. Ohne eigene Identitaet bleibt es vorerst beim bisherigen Weg. */
    // Eingetragen, aber nicht aufloesbar: kein stiller Wechsel auf ein geteiltes Testkonto.
    if (istPartner && identitaetFehler()) { setOpen(true); return; }
    const eigenes = istPartner ? envUsers.find(u => u.eigen && u.password)?.email ?? null : null;
    const vollstes = istPartner
      ? [...envUsers].filter(u => u.password && (kontoAnzahl(u) ?? 0) > 0).sort((a, b) => (kontoAnzahl(b) ?? 0) - (kontoAnzahl(a) ?? 0))[0]?.email ?? null
      : null;
    const def = eigenes ?? localStorage.getItem(defaultKey(appKey)) ?? vollstes;
    if (!def) { setOpen(true); return; }          // no default → show the list
    const user = envUsers.find((u) => u.email === def);
    if (!user) { setOpen(true); return; }
    autoTriedRef.current = appKey;
    void doLogin(user);
  }, [appKey, isLoginPage, isProduction, appCreds, doLogin, istPartner, credentialSet]);

  function toggleDefault(email: string) {
    if (!appKey) return;
    if (defaultEmail === email) {
      localStorage.removeItem(defaultKey(appKey));
      setDefaultEmail(null);
    } else {
      localStorage.setItem(defaultKey(appKey), email);
      setDefaultEmail(email);
    }
  }

  // Not a known app, or no credentials for it → render nothing.
  const kopiereText = async (marke: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Webview ohne Clipboard-Recht: ueber ein temporaeres Feld kopieren,
      // statt still zu scheitern.
      const feld = document.createElement('textarea');
      feld.value = text;
      feld.style.position = 'fixed';
      feld.style.opacity = '0';
      document.body.appendChild(feld);
      feld.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(feld); }
    }
    setKopiert(marke);
    window.setTimeout(() => setKopiert((m) => (m === marke ? null : m)), 1500);
  };

  // Zwei Knoepfe je Zeile: Schluessel klappt das Passwort auf, Klemmbrett kopiert
  // E-Mail und Passwort in EINEM Schritt (Rafael gibt beides zusammen weiter).
  const passwortFeld = (email: string, password: string | null) => {
    if (!password) return null;
    const offen = !!pwOffen[email];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={() => void kopiereText(`${email}|beides`, `${email}\n${password}`)}
          title="E-Mail und Passwort zusammen kopieren"
          style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0,
            color: kopiert === `${email}|beides` ? 'var(--tn-green, var(--tnf-9ece6a))' : 'var(--tn-border)',
          }}>
          {kopiert === `${email}|beides` ? '\u2713' : '\u{1F4CB}'}
        </button>
        {offen && (
          <button
            onClick={() => void kopiereText(`${email}|pw`, password)}
            title="Anklicken kopiert das Passwort"
            style={{
              background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', borderRadius: 3,
              color: 'var(--tn-text)', fontFamily: 'monospace', fontSize: 10, padding: '2px 6px',
              cursor: 'pointer', userSelect: 'all', whiteSpace: 'nowrap',
            }}>
            {kopiert === `${email}|pw` ? 'kopiert ✓' : password}
          </button>
        )}
        <button
          onClick={() => setPwOffen((st) => ({ ...st, [email]: !st[email] }))}
          title={offen ? 'Passwort verbergen' : 'Passwort zeigen und kopieren'}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0,
            color: offen ? 'var(--tn-yellow, var(--tnf-e0af68))' : 'var(--tn-border)',
          }}>
          🔑
        </button>
      </div>
    );
  };

  if (!appKey) return null;
  if (!appCreds) return <div data-ai-id="app-login-zugang-fehler" role={credentialsError ? 'alert' : 'status'}
    style={{ padding: '6px 10px', fontSize: 11, color: 'var(--tn-text)', background: 'var(--tn-bg)' }}>
    {credentialsError ? `App-Anmeldung nicht geladen (${credentialsError}).`
      : apps ? 'Für diese App sind noch keine Anmeldezugänge hinterlegt.' : 'App-Anmeldung wird geladen …'}
    <button type="button" onClick={() => { vergissAppZugaenge(); setCredentialsReload(n => n + 1); }}
      style={{ marginLeft: 8 }}>Erneut laden</button>
  </div>;

  if (gesperrt) return <div data-ai-id="app-login-gesperrt" role="status"
    style={{ padding: '6px 10px', fontSize: 11, color: 'var(--tn-text)', background: 'var(--tn-bg)' }}>
    Engelmann-Staging ist für dich nicht freigegeben: dort liegen echte Kundendaten.
  </div>;

  const q = search.trim().toLowerCase();
  /* Konten mit Daten zuerst (Rafael: „sonst sieht er ja nichts"). */
  const users = envUsers
    .filter((u) => !q || `${u.email} ${u.name || ''} ${u.role || ''}`.toLowerCase().includes(q))
    .sort((a, b) =>
      (b.eigen ? 1 : 0) - (a.eigen ? 1 : 0) ||
      ((kontoAnzahl(b) ?? 0) > 0 ? 1 : 0) - ((kontoAnzahl(a) ?? 0) > 0 ? 1 : 0) ||
      (b.primary ? 1 : 0) - (a.primary ? 1 : 0) ||
      (a.name || a.email).localeCompare(b.name || b.email),
    );
  const prodFiltered = (prodUsers ?? []).filter(
    (u) => !q || `${u.email} ${u.name || ''} ${u.role || ''}`.toLowerCase().includes(q),
  );

  if (!open) {
    /* Der eingeklappte Knopf lag als senkrechter Reiter oben rechts UEBER der App-Seite und verdeckte dort deren
     * Fensterknoepfe (Vollbild, Loesen, Schliessen) — Rafael 02.11.2026 (b-w-report): „Login-Button rechts liegt
     * meist über dem X zum Schließen.“ Er sitzt deshalb in der Adresszeile; ohne Adresszeile nur auf der Login-Seite. */
    if (adresszeilenPlatz) {
      return createPortal(
        <button
          type="button"
          data-ai-id="browser-panel-login"
          onClick={() => setOpen(true)}
          title={`Login — ${appCreds.name}`}
          style={{
            background: 'none', border: '1px solid var(--tn-border)', borderRadius: 3,
            color: 'var(--tn-text)', cursor: 'pointer', fontSize: 11, padding: '1px 6px', whiteSpace: 'nowrap',
          }}
        >
          🔑 Login{status ? ' …' : ''}
        </button>,
        adresszeilenPlatz,
      );
    }
    if (!isLoginPage) return null;
    return (
      <button
        onClick={() => setOpen(true)}
        title={`Login — ${appCreds.name}`}
        style={{
          position: 'absolute', top: 44, right: 0, zIndex: 20,
          background: 'var(--tn-bg-dark)', color: 'var(--tn-text)',
          border: '1px solid var(--tn-border)', borderRight: 'none',
          borderRadius: '6px 0 0 6px', padding: '8px 6px', cursor: 'pointer',
          fontSize: 11, writingMode: 'vertical-rl',
        }}
      >
        🔑 Login{status ? ' …' : ''}
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'absolute', top: 38, right: 0, bottom: 0, width: 300, zIndex: 20,
        display: 'flex', flexDirection: 'column',
        background: 'var(--tn-surface)', borderLeft: '1px solid var(--tn-border)',
        boxShadow: '-4px 0 12px rgba(0,0,0,0.3)',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 8px', borderBottom: '1px solid var(--tn-border)',
        background: 'var(--tn-bg-dark)', flexShrink: 0,
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tn-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          🔑 {appCreds.name}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* Local/Prod toggle — switches the webview's origin so the login goes
              against the local instance or the app's Prod-Bridge. */}
          <div style={{ display: 'flex', border: '1px solid var(--tn-border)', borderRadius: 4, overflow: 'hidden' }}>
            <button
              onClick={() => switchEnv(false)}
              disabled={!localUrl}
              title="Lokale Instanz öffnen"
              style={{
                background: !isProduction ? 'var(--tn-blue, var(--tnfh-7aa2f7))' : 'transparent',
                color: !isProduction ? 'var(--tnf-1a1b26)' : 'var(--tn-text-muted)',
                border: 'none', padding: '2px 7px', fontSize: 10, fontWeight: 600,
                cursor: localUrl ? 'pointer' : 'not-allowed',
              }}
            >Lokal</button>
            <button
              onClick={() => switchEnv(true)}
              disabled={!appCreds.productionUrl}
              title={appCreds.productionUrl ? `Production öffnen (${appCreds.productionUrl})` : 'Keine Production-URL hinterlegt'}
              style={{
                background: isProduction ? 'var(--tn-green, var(--tnfh-9ece6a))' : 'transparent',
                color: isProduction ? 'var(--tnf-1a1b26)' : 'var(--tn-text-muted)',
                border: 'none', borderLeft: '1px solid var(--tn-border)', padding: '2px 7px',
                fontSize: 10, fontWeight: 600,
                cursor: appCreds.productionUrl ? 'pointer' : 'not-allowed',
              }}
            >Prod</button>
          </div>
          <button
            onClick={() => setOpen(false)}
            style={{ background: 'none', border: 'none', color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 14 }}
            title="Schließen"
          >×</button>
        </div>
      </div>

      {status && (
        <div style={{ padding: '5px 8px', fontSize: 10, color: 'var(--tn-text-muted)', borderBottom: '1px solid var(--tn-border)' }}>
          {status}
        </div>
      )}

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={`${istProdSatz ? (prodUsers?.length ?? 0) : envUsers.length} ${istProdSatz ? 'Prod-' : ''}User durchsuchen …`}
        style={{
          margin: 8, background: 'var(--tn-bg)', color: 'var(--tn-text)',
          border: '1px solid var(--tn-border)', borderRadius: 4, padding: '4px 8px', fontSize: 11,
        }}
      />

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {/* Prod-Verzeichnis + Anlegen-Formular gehoeren zum PROD-SATZ, nicht zu
            jeder deployten Adresse: auf Staging sind das die falschen Konten. */}
        {istProdSatz ? (
          <>
            {/* Create-account affordance (Prod-Bridge register → trial license). */}
            {showCreate ? (
              <div style={{ padding: 8, borderBottom: '1px solid var(--tn-border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Name" style={createInputStyle} />
                <input value={createForm.email} onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="E-Mail" type="email" style={createInputStyle} />
                <input value={createForm.password} onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Passwort" style={createInputStyle} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => void createProdUser()} disabled={creating}
                    style={{ flex: 1, background: 'var(--tn-green, var(--tnfh-9ece6a))', color: 'var(--tnf-1a1b26)', border: 'none', borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 600, cursor: creating ? 'wait' : 'pointer' }}>
                    {creating ? 'Lege an …' : 'Anlegen + Login'}
                  </button>
                  <button onClick={() => setShowCreate(false)} disabled={creating}
                    style={{ background: 'none', color: 'var(--tn-text-muted)', border: '1px solid var(--tn-border)', borderRadius: 4, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}>
                    Abbrechen
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowCreate(true)}
                style={{ width: '100%', textAlign: 'left', background: 'none', color: 'var(--tn-blue, var(--tnf-7aa2f7))', border: 'none', borderBottom: '1px solid var(--tn-border)', padding: '8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                ＋ Account auf Production anlegen
              </button>
            )}

            {prodState === 'loading' && (
              <div style={{ padding: 12, fontSize: 11, color: 'var(--tn-text-muted)' }}>Lade Prod-Accounts …</div>
            )}
            {prodState === 'error' && (
              <div style={{ padding: 12, fontSize: 10, color: 'var(--tn-red, var(--tnf-f7768e))' }}>
                Bridge-Fehler: {prodError}
                <button onClick={() => void loadProdUsers()} style={{ marginLeft: 6, background: 'none', border: '1px solid var(--tn-border)', color: 'var(--tn-text)', borderRadius: 3, fontSize: 10, cursor: 'pointer', padding: '1px 6px' }}>neu laden</button>
              </div>
            )}

            {prodFiltered.map((u) => (
              <div key={u.id || u.email}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px',
                  borderBottom: '1px solid var(--tn-border)',
                  borderLeft: u.canLogin ? '2px solid var(--tn-green, var(--tnf-9ece6a))' : '2px solid transparent',
                  background: u.canLogin ? 'rgba(158, 206, 106, 0.08)' : undefined,
                }}>
                <button
                  onClick={() => u.canLogin && u.password && doLogin({ name: u.name, email: u.email, password: u.password, role: u.role, primary: false, tester: false, env: 'production' })}
                  disabled={!u.canLogin}
                  title={u.canLogin ? `Einloggen als ${u.email}` : 'Passwort unbekannt — nicht einloggbar (nur hier angelegte / hinterlegte Accounts sind klickbar)'}
                  style={{
                    flex: 1, textAlign: 'left', background: 'none', border: 'none',
                    cursor: u.canLogin ? 'pointer' : 'default',
                    color: u.canLogin ? 'var(--tn-text)' : 'var(--tn-text-muted)', padding: 0,
                  }}>
                  <div style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: u.canLogin ? 'var(--tn-green, var(--tnf-9ece6a))' : undefined }}>{u.name || u.email}</span>
                    {u.licensed && <span style={{ fontSize: 9, color: 'var(--tn-text-muted)' }}>lizenziert</span>}
                    {!u.canLogin && <span style={{ fontSize: 9, color: 'var(--tn-text-muted)' }}>🔒 kein PW</span>}
                    {u.pwDrift && (
                      <span title="Zwei Quellen hielten verschiedene Passwörter — hier steht das aus prod-credentials.json, das auch gilt."
                        style={{ fontSize: 9, color: 'var(--tn-yellow, var(--tnf-e0af68))' }}>⚠ PW war doppelt</span>
                    )}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--tn-text-muted)' }}>
                    {u.email}{u.role ? ` · ${u.role}` : ''}
                  </div>
                </button>
                {passwortFeld(u.email, u.password)}
              </div>
            ))}
            {prodState === 'idle' && prodFiltered.length === 0 && (
              <div style={{ padding: 12, fontSize: 11, color: 'var(--tn-text-muted)' }}>Keine Accounts auf der Prod-Bridge.</div>
            )}
          </>
        ) : (
          <>
            {users.map((u) => (
              <div
                key={u.email}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px',
                  borderBottom: '1px solid var(--tn-border)',
                  // GRUEN = DIESES KONTO HAT DATEN (Rafael, 05.10.2026). Bis dahin hiess gruen
                  // „kollisionsfrei"; das steht jetzt nur noch als kleines Abzeichen daneben.
                  borderLeft: (kontoAnzahl(u) ?? 0) > 0 ? '2px solid var(--tn-green, var(--tnf-9ece6a))' : '2px solid transparent',
                  background: (kontoAnzahl(u) ?? 0) > 0 ? 'rgba(158, 206, 106, 0.08)' : undefined,
                }}
              >
                <button
                  onClick={() => toggleDefault(u.email)}
                  title={defaultEmail === u.email ? 'Standard-User (Auto-Login)' : 'Als Standard setzen'}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: 13,
                    color: defaultEmail === u.email ? 'var(--tn-yellow, var(--tnf-e0af68))' : 'var(--tn-border)',
                  }}
                >★</button>
                <button
                  onClick={() => doLogin(u)}
                  style={{
                    flex: 1, textAlign: 'left', background: 'none', border: 'none',
                    cursor: 'pointer', color: 'var(--tn-text)', padding: 0,
                  }}
                >
                  <div style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: (kontoAnzahl(u) ?? 0) > 0 ? 'var(--tn-green, var(--tnf-9ece6a))' : undefined }}>{u.name || u.email}</span>
                    {u.primary && <span style={{ fontSize: 9, color: 'var(--tn-text-muted)' }}>primär</span>}
                    {u.eigen && <span data-ai-id="app-login-eigene-identitaet" style={{ fontSize: 9, fontWeight: 600, color: 'var(--tn-blue, var(--tnf-7aa2f7))' }}>deine Identität</span>}
                    {!u.tester && !u.identitaet && <span style={{ fontSize: 9, color: 'var(--tn-text-muted)' }}>kollisionsfrei</span>}
                    {Object.entries(u.daten ?? {}).map(([ziel, d]) => (
                      <span key={ziel} data-ai-id="app-login-konto-daten"
                        title={'fehler' in d ? `Messung ${ziel}: ${d.fehler}` : `${ziel === 'demo' ? 'Partner-Demo' : 'Staging'}: ${d.anzahl} ${d.was}`}
                        style={{ fontSize: 9, fontWeight: 600, color: 'fehler' in d ? 'var(--tn-text-muted)' : d.anzahl > 0 ? 'var(--tn-green, var(--tnf-9ece6a))' : 'var(--tn-text-muted)' }}>
                        {ziel === 'demo' ? 'Demo' : 'Staging'} {'fehler' in d ? '⚠' : `${d.anzahl} ${d.was}`}
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--tn-text-muted)' }}>
                    {u.email}{u.role ? ` · ${u.role}` : ''}
                  </div>
                </button>
                {passwortFeld(u.email, u.password)}
              </div>
            ))}
            {users.length === 0 && (
              <div style={{ padding: 12, fontSize: 11, color: 'var(--tn-text-muted)' }}>
                Kein Treffer.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const createInputStyle: React.CSSProperties = {
  background: 'var(--tn-bg)', color: 'var(--tn-text)',
  border: '1px solid var(--tn-border)', borderRadius: 4, padding: '4px 8px', fontSize: 11,
};
